import { CommonBonusDetails } from "../../models/responses/commonBonusDetails";
import localization from "../../../../config/localization";
import { formatUtcToLocalDateTime } from "../../../../utils/dateTimeHelper";
import { BonusConsts } from "../../models/constants/bonusConsts";
import { useState } from "react";
import { Button } from "../../../../components/ui";
import { checkAndActivateFreeSpinBonus } from "../../services/bonus.service";
import { QtechFreeSpin } from "../../../games/services/qtech.service";
import { useNavigate } from "react-router-dom";
import { localStorageService } from "../../../../services";
import userDeviceStore from "../../../../store/device.store";

interface FreeSpinBonusCardProps {
  item: CommonBonusDetails;
  onClaimed?: () => void;
}

export default function FreeSpinBonus({
  item,
  onClaimed,
}: FreeSpinBonusCardProps) {
  const [isClaiming, setIsClaiming] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const navigate = useNavigate();
  const accessModeSource = userDeviceStore((state) => state.accessModeSource);

  const handleClaim = async () => {
    if (isClaiming) return;
    setIsClaiming(true);
    try {
      const success = await checkAndActivateFreeSpinBonus(
        item.bonuswallettransactionid,
        accessModeSource.sourceType,
      );
      if (success && onClaimed) {
        onClaimed();
      }
    } finally {
      await new Promise((r) => setTimeout(r, 1300));
      setIsClaiming(false);
    }
  };

  const handleGameClick = async () => {
    if (isExecuting) return;
    setIsExecuting(true);
    try {
      const userId = localStorageService.getItem<string>("providerUserId");
      if (userId && item.gameId && item.gameCode) {
        const launchUrl = await QtechFreeSpin(
          userId,
          item.gameId,
          item.gameCode,
          accessModeSource.sourceType,
        );
        if (launchUrl) {
          navigate("/gamelaunch", { state: { launchUrl } });
        }
      }
    } finally {
      setIsExecuting(false);
    }
  };

  return (
    <div className="w-full bg-[#010101] border border-[#353535] rounded-[4px] p-3">
      <div className="grid gap-4 text-white">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img
              src="/icons/Bonus/ic-freespin.svg"
              alt="Free Spin"
              className="size-6"
            />
            <span className="font-medium text-sm text-[#ECECFF]">
              {localization.free_spin}
            </span>
          </div>
          {item.status === BonusConsts.ActiveStatus && (
            <img
              src="/icons/Bonus/ic-freespin-claimed-text.svg"
              alt="Claimed"
              className="h-5"
            />
          )}
          {item.status === "InActive" && (
            <Button
              onClick={handleClaim}
              disabled={isClaiming}
              style={{
                background: "linear-gradient(180deg, #650687 0%, #4C0067 100%)",
              }}
              className="text-white text-sm font-medium px-4 py-1 rounded"
            >
              {localization.claim}
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <span className="text-lg">{item.bonusname}</span>
        </div>
        <div className="flex justify-between items-end">
          <div>
            <p
              className={`text-base font-medium ${isExecuting ? "opacity-50 cursor-default" : "cursor-pointer text-[#ECECFF]"}`}
              onClick={!isExecuting ? handleGameClick : undefined}
            >
              {item.gamename}
            </p>
            <p className="text-xs font-medium text-[#A9A9A9]">
              {localization.game_name}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="text-base font-medium">
              {`${item.bonusamount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}₹`}
            </span>{" "}
            <p className="text-xs font-medium text-[#A9A9A9]">
              {localization.amount_won}
            </p>
          </div>
          <div className="float-right text-left">
            <span className="text-base font-medium">
              {item.status === BonusConsts.ActiveStatus
                ? `${item.remainingActiveFreeSpinCount}/${item.freespincount}`
                : item.status === BonusConsts.ConsumedStatus
                  ? `${item.consumedfreespincount}/${item.freespincount}`
                  : `${item.expiredfreespincount}/${item.freespincount}`}
            </span>
            <p className="text-xs font-medium text-[#A9A9A9]">
              {localization.freespin_bonus_step}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <span className="text-base font-medium">
              {item.claimingdate
                ? formatUtcToLocalDateTime(item.claimingdate, false)
                : "-"}
            </span>
            <p className="text-xs font-medium text-[#A9A9A9]">
              {localization.bonus_claiming_date}
            </p>
          </div>
          <div>
            <span className="text-base font-medium">
              {item.status === BonusConsts.ConsumedStatus
                ? formatUtcToLocalDateTime(item.modified, false)
                : formatUtcToLocalDateTime(item.bonusexpiredate, false)}
            </span>
            <p className="text-xs font-medium text-[#A9A9A9] text-left">
              {item.status === BonusConsts.ConsumedStatus
                ? localization.bonusconsumeddate
                : item.status === BonusConsts.CancelledStatus
                  ? localization.bonuscanceldate
                  : item.status === BonusConsts.ActiveStatus ||
                      item.status === BonusConsts.InActiveStatus
                    ? localization.bonusexpiringdate
                    : localization.bonusexpireddate}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
